<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import WysiwygEditor from '../components/WysiwygEditor.vue'
import paymentDocumentService from '../services/payment-document.service'
import api from '../services/api'

const router = useRouter()
const route = useRoute()
const { t, locale } = useI18n()

const clubUuid = route.params.uuid as string
const documentType = route.params.type as 'terms' | 'privacy' | 'other'

const form = ref({
  title: '',
  name: '',
  content: ''
})

const loading = ref(false)
const loadingDraft = ref(false)
const isDraft = ref(false)
const error = ref<string | null>(null)

onMounted(async () => {
  if (documentType === 'terms' || documentType === 'privacy') {
    loadingDraft.value = true
    try {
      const response = await api.get(`v1/${clubUuid}/payment-documents/draft/${documentType}`, {
        headers: { 'Accept-Language': locale.value }
      })
      if (response.data?.draft && response.data?.content) {
        form.value.content = response.data.content
        isDraft.value = true
      }
    } catch {
      // Draft not available — start with empty editor
    } finally {
      loadingDraft.value = false
    }
  }
})

const pageTitle = computed(() => {
  switch (documentType) {
    case 'terms':
      return t('paymentDocumentCreate.termsTitle')
    case 'privacy':
      return t('paymentDocumentCreate.privacyTitle')
    case 'other':
      return t('paymentDocumentCreate.addDocument')
    default:
      return t('paymentDocumentCreate.addDocument')
  }
})

const defaultTitle = computed(() => {
  switch (documentType) {
    case 'terms':
      return t('paymentDocumentCreate.termsTitle')
    case 'privacy':
      return t('paymentDocumentCreate.privacyTitle')
    default:
      return ''
  }
})

const needsName = computed(() => documentType === 'other')

const validateForm = (): string | null => {
  if (documentType === 'other' && !form.value.name.trim()) {
    return t('paymentDocumentCreate.nameRequired')
  }

  if (!form.value.content.trim() || form.value.content === '<p></p>') {
    return t('paymentDocumentCreate.contentRequired')
  }

  return null
}

const handleSave = async () => {
  error.value = null

  const validationError = validateForm()
  if (validationError) {
    error.value = validationError
    return
  }

  loading.value = true

  try {
    const data = {
      type: documentType,
      title: documentType === 'other'
        ? form.value.name.trim()
        : (form.value.title.trim() || defaultTitle.value),
      content: form.value.content,
      ...(documentType === 'other' && { name: form.value.name.trim() })
    }

    await paymentDocumentService.createDocument(clubUuid, data)

    router.push(`/club/${clubUuid}/payment-settings`)
  } catch (err: any) {
    console.error('Error creating document:', err)
    error.value = err.response?.data?.message || t('paymentDocumentCreate.errorSaving')
  } finally {
    loading.value = false
  }
}

const handleCancel = () => {
  router.push(`/club/${clubUuid}/payment-settings`)
}
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-6">
      <!-- Back Button -->
      <div class="mb-4">
        <button
          @click="handleCancel"
          class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('paymentDocumentCreate.backToSettings') }}
        </button>
      </div>

      <!-- Page Header -->
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-gray-900">{{ pageTitle }}</h1>
      </div>

      <!-- Draft Warning Banner -->
      <div v-if="isDraft" class="mb-4 bg-red-600 border-2 border-red-800 rounded-lg p-4 text-center">
        <p class="text-white font-bold text-base uppercase tracking-wide">
          ⚠ {{ t('paymentDocumentCreate.draftWarning') }} ⚠
        </p>
      </div>

      <!-- Draft Loading -->
      <div v-if="loadingDraft" class="mb-4 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg p-3">
        <p class="text-sm">{{ t('paymentDocumentCreate.loadingDraft') }}</p>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="mb-4 bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
        <p class="text-sm">{{ error }}</p>
      </div>

      <!-- Form -->
      <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
        <form @submit.prevent="handleSave" class="space-y-6">
          <!-- Title (optional for terms/privacy, shown for all) -->
          <div v-if="documentType !== 'other'">
            <label for="title" class="block text-sm font-medium text-gray-700 mb-2">
              {{ t('paymentDocumentCreate.documentTitleLabel') }}
            </label>
            <input
              id="title"
              v-model="form.title"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              :placeholder="defaultTitle"
            />
            <p class="mt-1 text-sm text-gray-500">
              {{ t('paymentDocumentCreate.documentTitleHint', { title: defaultTitle }) }}
            </p>
          </div>

          <!-- Name (required for "other" type) -->
          <div v-if="needsName">
            <label for="name" class="block text-sm font-medium text-gray-700 mb-2">
              {{ t('paymentDocumentCreate.documentNameLabel') }} <span class="text-red-600">*</span>
            </label>
            <input
              id="name"
              v-model="form.name"
              type="text"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              :placeholder="t('paymentDocumentCreate.documentNamePlaceholder')"
            />
          </div>

          <!-- Content (WYSIWYG Editor) -->
          <div>
            <label for="content" class="block text-sm font-medium text-gray-700 mb-2">
              {{ t('paymentDocumentCreate.contentLabel') }} <span class="text-red-600">*</span>
            </label>
            <WysiwygEditor
              v-model="form.content"
              :placeholder="t('paymentDocumentCreate.contentPlaceholder')"
            />
          </div>

          <!-- Action Buttons -->
          <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              @click="handleCancel"
              :disabled="loading"
              class="px-6 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ t('common.cancel') }}
            </button>
            <button
              type="submit"
              :disabled="loading"
              class="px-6 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ loading ? t('paymentDocumentCreate.saving') : t('common.save') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </main>
</template>
